import { PushPin } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { categoryThumbUrl } from '@/lib/category-covers'

import type { ChatListItem } from '../format'
import { maxBadgeCount, privateDisplayName } from '../format'
import { ChatAvatar } from './chat-avatar'
import { ChatMenu, type ChatMenuItem } from './chat-menu'

interface ChatRowProps {
  item: ChatListItem
  pinned: boolean
  unreadCount: number
  onPin: () => void
  onDelete?: () => void
  busy?: boolean
}

/**
 * One merged chat row. The primary area is a link; the overflow menu is a
 * sibling so its buttons are never nested inside the link.
 */
export function ChatRow({
  item,
  pinned,
  unreadCount,
  onPin,
  onDelete,
  busy = false,
}: ChatRowProps) {
  const { t } = useTranslation()

  const isActivity = item.kind === 'activity'
  const title = isActivity
    ? item.chat.title
    : privateDisplayName(item.conversation)
  const subtitle =
    (isActivity ? item.chat.lastMessage : item.conversation.lastMessage) ??
    t('chat.list.startConversation')
  const href = isActivity ? `/chat/${item.id}` : `/messages/${item.id}`
  const linkLabel = [
    unreadCount > 0
      ? t('chat.list.openUnread', { name: title, count: unreadCount })
      : t('chat.list.open', { name: title }),
    ...(pinned ? [t('chat.list.pinned')] : []),
  ].join(', ')

  const menuItems: ChatMenuItem[] = [
    {
      key: 'pin',
      labelKey: pinned ? 'chat.actions.unpin' : 'chat.actions.pin',
      onSelect: onPin,
      disabled: busy,
    },
  ]
  if (onDelete) {
    menuItems.push({
      key: 'delete',
      labelKey: 'chat.actions.delete',
      onSelect: onDelete,
      danger: true,
      disabled: busy,
    })
  }

  return (
    <li className="chat-row" data-unread={unreadCount > 0 ? 'true' : 'false'}>
      <Link
        to={href}
        className="chat-row__link"
        aria-label={linkLabel}
      >
        <span className="chat-row__media">
          {isActivity ? (
            <img
              src={categoryThumbUrl(
                item.chat.categorySlug,
                item.chat.categoryName,
              )}
              alt=""
              loading="lazy"
              decoding="async"
              className="chat-row__cover"
            />
          ) : (
            <ChatAvatar
              name={privateDisplayName(item.conversation)}
              src={item.conversation.avatarUrl}
              size="md"
            />
          )}
        </span>

        <span className="chat-row__body">
          <span className="chat-row__top">
            <span className="chat-row__title">{title}</span>
          </span>
          <span className="chat-row__bottom">
            <span className="chat-row__subtitle">{subtitle}</span>
            {unreadCount > 0 ? (
              <span className="chat-row__meta">
                <span className="chat-row__badge" aria-hidden="true">
                  {maxBadgeCount(unreadCount)}
                </span>
                <span className="sr-only">
                  {t('chat.list.unread', { count: unreadCount })}
                </span>
              </span>
            ) : null}
            {pinned ? (
              <span className="chat-row__pin">
                <PushPin size={16} weight="bold" aria-hidden="true" />
                <span className="sr-only">{t('chat.list.pinned')}</span>
              </span>
            ) : null}
          </span>
        </span>
      </Link>

      <ChatMenu
        items={menuItems}
        label={t('chat.actions.optionsFor', { name: title })}
        className="chat-row__menu"
      />
    </li>
  )
}
